/**
 * Role helpers — the creator-platform authorization primitives. Roles come from
 * Firebase custom claims (`request.auth.token.role`), per
 * docs/CREATOR-PLATFORM.md decision 7. This module is the client/service-side
 * mirror of the rules-level checks so callers can gate an action BEFORE a write
 * the rules would reject anyway (defense in depth — the rules stay the second
 * line).
 */
import { PlatformError, type Uid } from '@djgg/core';

/**
 * The two privileged platform roles. A user with no `role` claim is a plain
 * authenticated user: they can create and collaborate, but cannot administer.
 */
export type Role = 'creator' | 'superadmin';

/**
 * The auth-claim shape these helpers read — the decoded token's relevant
 * fields. `uid` is always present for a signed-in user; `role` is the optional
 * custom claim.
 */
export interface AuthClaims {
  uid: Uid;
  role?: Role;
}

/**
 * True when `claims` carries at least the given role. `superadmin` implies
 * `creator` (an admin can do anything a creator can), so
 * `hasRole(admin, 'creator')` is true.
 */
export function hasRole(claims: AuthClaims | null | undefined, role: Role): boolean {
  if (!claims) return false;
  if (claims.role === 'superadmin') return true;
  return claims.role === role;
}

/**
 * Assert `claims` carries `role`, else throw `PlatformError('permission')`.
 * Returns the claims narrowed non-null for chaining.
 */
export function requireRole(claims: AuthClaims | null | undefined, role: Role): AuthClaims {
  if (!claims) {
    throw new PlatformError('permission', `requireRole(${role}): not signed in`);
  }
  if (!hasRole(claims, role)) {
    throw new PlatformError(
      'permission',
      `requireRole(${role}): user ${claims.uid} has role ${claims.role ?? 'none'}`,
    );
  }
  return claims;
}

/**
 * True when the caller owns `ownerUid` OR is a superadmin. The canonical check
 * for "may edit/delete this creation" — the owner or an admin, nobody else.
 */
export function isOwnerOrAdmin(claims: AuthClaims | null | undefined, ownerUid: Uid): boolean {
  if (!claims) return false;
  return claims.uid === ownerUid || claims.role === 'superadmin';
}

/**
 * Assert the caller may mutate a resource owned by `ownerUid` (owner or admin),
 * else throw `PlatformError('permission')`.
 */
export function requireOwnerOrAdmin(
  claims: AuthClaims | null | undefined,
  ownerUid: Uid,
): AuthClaims {
  if (!isOwnerOrAdmin(claims, ownerUid)) {
    throw new PlatformError(
      'permission',
      `requireOwnerOrAdmin: ${claims?.uid ?? 'anon'} may not mutate resource owned by ${ownerUid}`,
    );
  }
  return claims as AuthClaims;
}

/**
 * The membership slice of a creation these helpers read (structural, so both
 * a full `Creation` and a test stub fit). `memberUids` is the server-owned
 * `[creatorUid, ...editor uids]` index; docs that predate collaboration may
 * lack it, so `creatorUid` is always consulted as the fallback.
 */
export interface CreationMembership {
  creatorUid: Uid;
  memberUids?: Uid[];
}

/**
 * True when the caller is a MEMBER of the creation (its creator or one of its
 * editors) OR a superadmin. The collaboration-era check for "may edit this
 * creation" — build, iterate, set active version, update metadata. Deleting
 * (versions or the creation) and roster management stay
 * {@link isOwnerOrAdmin}-gated.
 */
export function isMemberOrAdmin(
  claims: AuthClaims | null | undefined,
  creation: CreationMembership,
): boolean {
  if (!claims) return false;
  if (claims.role === 'superadmin') return true;
  if (claims.uid === creation.creatorUid) return true;
  return creation.memberUids?.includes(claims.uid) ?? false;
}

/**
 * Assert the caller may edit the creation (member or admin), else throw
 * `PlatformError('permission')`.
 */
export function requireMemberOrAdmin(
  claims: AuthClaims | null | undefined,
  creation: CreationMembership,
): AuthClaims {
  if (!isMemberOrAdmin(claims, creation)) {
    throw new PlatformError(
      'permission',
      `requireMemberOrAdmin: ${claims?.uid ?? 'anon'} is not a member of the creation owned by ${creation.creatorUid}`,
    );
  }
  return claims as AuthClaims;
}

/**
 * Platform operators — the allowlist for internal operator surfaces: Admin
 * Stats (`/admin/stats` + `getAdminStats`) and the FULL-DETAIL build/asset
 * trace tier (unredacted prompts/responses/judge verdicts + the prompt-log
 * signed-URL resolver — trace-explorer initiative, owner interview
 * 2026-07-24). Superadmins always qualify; these named creator-role
 * developers additionally do.
 *
 * This is the ONE source of the allowlist for every TS consumer (portal route
 * guards and the callable handlers). The gated tables are RLS deny-all /
 * server-read-only, so there is deliberately NO uid list duplicated into SQL
 * to drift against — changing the allowlist is a one-line edit here, no
 * migration.
 */
export const PLATFORM_OPERATORS: readonly Uid[] = [
  'afYPIpBDpWZRy4XnRl03Gnlrf972', // jon@djgg.dev
  'zsSwwu9QQqM9gmYuubrmmtWSBF82', // donald@djgg.dev
];

/** True when the caller is a platform operator (superadmin or allowlisted uid). */
export function isPlatformOperator(claims: AuthClaims | null | undefined): boolean {
  if (!claims) return false;
  if (claims.role === 'superadmin') return true;
  return PLATFORM_OPERATORS.includes(claims.uid);
}

/** Assert the caller is a platform operator, else throw `PlatformError('permission')`. */
export function requirePlatformOperator(claims: AuthClaims | null | undefined): AuthClaims {
  if (!isPlatformOperator(claims)) {
    throw new PlatformError(
      'permission',
      `requirePlatformOperator: ${claims?.uid ?? 'anon'} is not a platform operator`,
    );
  }
  return claims as AuthClaims;
}
